import gql from 'graphql-tag';
import { OFFER_FRAGMENT } from 'core/fragments/offer';

export const GET_OFFER = gql`
  query getOffer($offerId: ID!) {
    offer: getOffer(id: $offerId) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const GET_EXISTING_CUSTOMER_POLICIES = gql`
  query getExistingCustomerPolicies($id: ID!) {
    account: getAccount(id: $id) {
      policies {
        id
        policyType
        state
        offer {
          ...OfferFragment
        }
        segments {
          segmentId
          startDate
          drivers {
            id
            isPrimary
            insuranceScore {
              autoTotal
              homeTotal
            }
          }
          cars {
            VIN
          }
          global {
            affinity
            continuousAutoCoverageStartDate
          }
          autoCoverage {
            policyLimitBIPD
          }
        }
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const RECALCULATE_QUOTE_TO_CLUSTER = gql`
  query recalculateQuoteToCluster($offerId: ID!, $revisedQuoteDetails: QuoteDetailsInput!) {
    cluster: recalculateQuoteToCluster(offerId: $offerId, revisedQuoteDetails: $revisedQuoteDetails) {
      id
      offers {
        ...OfferFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const REQUEST_BIND = gql`
  mutation ($request: BindRequestInput!) {
    requestBind(request: $request) {
      id
      systemId
    }
  }
`;

export const ADD_REFERRAL = gql`
  mutation addReferral($referralDetails: ReferralDetailsInput) {
    friendBuyLink: addReferral(referralDetails: $referralDetails)
  }
`;

export const DOWNLOAD_QUOTE = gql`
  mutation generateApplicationDraft($details: QuotePdfInput) {
    quotePdf: generateApplicationDraft(details: $details)
  }
`;

export const DOWNLOAD_OFFER = gql`
  query getOfferPdf($documentUrl: String!, $documentId: String!, $bypassCache: Boolean) {
    offerPdf: getDocraptorDocument(documentUrl: $documentUrl, documentId: $documentId, bypassCache: $bypassCache)
  }
`;

export const GET_POLICY_FROM_OFFER_ID = gql`
  query ($offerId: ID!) {
    getPolicyFromOfferId(offerId: $offerId) {
      accountId
      id
    }
  }
`;

export const ADD_DRIVERS_CARS_AND_RECALCULATE = gql`
  mutation addDriversAndCarsAndRecalculate(
    $offerId: ID!
    $newDrivers: [PersonDetailsInput]
    $newVins: [CarDetailsInput]
    $newTrailerVins: [TrailerDetailsInput]
    $revisedQuoteDetails: QuoteDetailsInput
  ) {
    cluster: addCarsAddDriversAndRecalculateQuoteToCluster(
      offerId: $offerId
      newDrivers: $newDrivers
      newVins: $newVins
      newTrailerVins: $newTrailerVins
      revisedQuoteDetails: $revisedQuoteDetails
    ) {
      id
      offers {
        ...OfferFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const ADD_TO_PRE_BIND_UW_REJECTIONS = gql`
  mutation ($webUserId: ID!, $ineligibleForHome: Boolean!, $ineligibleForAuto: Boolean!, $attestationAgent: String!) {
    addToPreBindUWRejections(
      webUserId: $webUserId
      ineligibleForHome: $ineligibleForHome
      ineligibleForAuto: $ineligibleForAuto
      attestationAgent: $attestationAgent
    ) {
      webUserId
      ineligibleForHome
      ineligibleForAuto
      attestationAgent
    }
  }
`;

export const GET_PRE_BIND_UW_REJECTIONS_BY_WEB_USER_ID = gql`
  query ($webUserId: ID!) {
    getPreBindUWRejectionsByWebUserId(webUserId: $webUserId) {
      webUserId
      ineligibleForHome
      ineligibleForAuto
    }
  }
`;

export const GET_CAR_DRIVER_SUGGESTIONS = gql`
  query getCarDriverSuggestions($offerId: ID!) {
    driverSuggestions: getDriverSuggestions(offerId: $offerId) {
      id
      firstName
      lastName
      birthDate
      gender
      maritalStatus
      driversLicenseState
      ageFirstLicensed
      age
    }

    carSuggestions: getCarSuggestions(offerId: $offerId) {
      VIN
      year
      make
      model
    }
  }
`;

export const CLEAR_REJECT_CODES = gql`
  mutation clearRejectCodes($offerId: ID!, $invokedFromStaff: Boolean) {
    offer: clearRejectCodes(offerId: $offerId, invokedFromStaff: $invokedFromStaff) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const SWAP_HOME_RENTERS = gql`
  query swapHomeRentersPolicy($offerId: ID!, $revisedQuoteDetails: QuoteDetailsInput!) {
    offer: swapHomeRentersPolicy(offerId: $offerId, revisedQuoteDetails: $revisedQuoteDetails) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const FILL_OUT_SKIPPED_DATA_PULLS = gql`
  query fillOutSkippedDataPullsRecalculateQuoteToCluster(
    $offerId: ID!
    $invokedFromStaff: Boolean
    $dataPulls: [String]
  ) {
    cluster: fillOutSkippedDataPullsRecalculateQuoteToCluster(
      offerId: $offerId
      invokedFromStaff: $invokedFromStaff
      dataPulls: $dataPulls
    ) {
      id
      offers {
        ...OfferFragment
      }
    }
  }
  ${OFFER_FRAGMENT}
`;

export const SEND_PURCHASE_LINK = gql`
  mutation sendPurchaseLink($webUserId: ID!, $offerId: ID!, $purchaseInformation: PurchaseInformationInput!) {
    offer: sendPurchaseLink(webUserId: $webUserId, offerId: $offerId, purchaseInformation: $purchaseInformation) {
      ...OfferFragment
    }
  }
  ${OFFER_FRAGMENT}
`;

export const GET_MERGED_OFFER = gql`
  query getMergedOffer($offerId: ID!, $rater: String) {
    offer: getMergedOffer(offerId: $offerId, rater: $rater) {
      id
      usingNonVerifiedData
    }
  }
`;

export const GET_OFFER_DIFFERENCE = gql`
  query getOfferDifference($oldOfferId: ID!, $newOfferId: ID!) {
    getOfferDifference(oldOfferId: $oldOfferId, newOfferId: $newOfferId) {
      success
      difference {
        addedChanges
        removedChanges
        diffKeys {
          full
          filtered
        }
        diff {
          full {
            addedChanges
            removedChanges
          }
          filtered {
            addedChanges
            removedChanges
          }
        }
        eligibilityChanges {
          policyType
          wasEligible
          isEligible
          rejectionCodesAdded
          rejectionCodesRemoved
        }
      }
      error
    }
  }
`;

export const UPDATE_OFFER_ADVANCED_CONFIGURATION = gql`
  mutation updateOfferAdvancedConfiguration($offerId: ID!, $skipUnderwritingUntil: AWSDateTime) {
    updateOfferAdvancedConfiguration(offerId: $offerId, skipUnderwritingUntil: $skipUnderwritingUntil) {
      success
      message
    }
  }
`;
